import React from "react";
import { motion } from "motion/react";

import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface ImpressiveTooltipProps {
  label: string;
  description?: string;
  icon?: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  align?: React.ComponentProps<typeof TooltipContent>["align"];
  children: React.ReactNode;
}

const defaultIcon = (
  <span className="relative flex h-3 w-3">
    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-400 via-slate-200 to-white opacity-80" />
    <span className="relative inline-flex h-full w-full rounded-full bg-slate-600/70 dark:bg-slate-300/80" />
  </span>
);

export const ImpressiveTooltip: React.FC<ImpressiveTooltipProps> = ({
  label,
  description,
  icon,
  side = "top",
  align = "center",
  children,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={12}
        className="bg-transparent p-0 shadow-none border-none text-left [&>svg]:hidden"
      >
        <motion.div
          initial={{ opacity: 0, y: 6, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.96 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white/95 via-slate-50/90 to-white/95 px-4 py-3 text-slate-800 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-700/80 dark:from-slate-900/95 dark:via-slate-900/95 dark:to-slate-800/90 dark:text-slate-100"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 shadow-inner shadow-white/40 dark:bg-slate-800 dark:text-slate-200">
            {icon ?? defaultIcon}
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-semibold tracking-wide">{label}</span>
            {description && <span className="text-[11px] text-slate-500 dark:text-slate-400">{description}</span>}
          </div>
        </motion.div>
      </TooltipContent>
    </Tooltip>
  );
};
